<script setup lang="ts">
import EuroRailApp from '@/components/app/EuroRailApp.vue';

/**
 * Racine de l'app interactive EuroRail.
 *
 * Cette app est embarquée sur `/carte-ferroviaire-europe-interactive/`.
 * Le site éditorial (pages SEO, guides, comparateurs, pays, itinéraires)
 * est servi à la racine `/` par un projet Astro séparé.
 *
 * Pas de routeur ici : une seule "page" — la carte. Les liens vers le site
 * éditorial pointent vers `/` (le serveur web fait le reste).
 */
const SITE_URL = '/';

// Klaro est chargé via index.html. On l'invoque ici quand l'utilisateur
// clique sur « Gérer les cookies ». Window est typée any pour éviter d'ajouter
// une définition globale pour une seule fonction.
function openCookieSettings() {
  const w = window as unknown as { klaro?: { show: () => void } };
  if (w.klaro && typeof w.klaro.show === 'function') {
    w.klaro.show();
  }
}
</script>

<template>
  <!-- Header minimal : lien retour vers le site éditorial -->
  <header class="site-header">
    <div class="site-header__inner">
      <a :href="SITE_URL" class="site-header__brand" aria-label="EuroRail — retour à l'accueil">
        <span class="site-header__logo" aria-hidden="true">
          <i class="ti ti-train"></i>
        </span>
        <span class="site-header__brand-text">
          <span class="site-header__name">EuroRail</span>
          <span class="site-header__back">← Retour au site</span>
        </span>
      </a>

      <button
        type="button"
        class="site-header__cookies"
        title="Gérer le consentement aux cookies"
        @click="openCookieSettings"
      >
        <i class="ti ti-cookie" aria-hidden="true"></i>
        <span class="site-header__cookies-label">Cookies</span>
      </button>
    </div>
  </header>

  <!-- App -->
  <main>
    <EuroRailApp />
  </main>
</template>

<style scoped>
.site-header {
  height: var(--site-header-height);
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 2000;
}

.site-header__inner {
  height: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.site-header__brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--text);
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  padding: 6px 10px 6px 6px;
  border-radius: 8px;
  transition: background 160ms;
}

.site-header__brand:hover {
  background: var(--surface-muted);
}

.site-header__logo {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: var(--accent);
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.site-header__brand-text {
  display: inline-flex;
  flex-direction: column;
  line-height: 1.2;
}

.site-header__name {
  font-weight: 600;
  font-size: 15px;
}

.site-header__back {
  font-size: 11px;
  color: var(--text-muted);
}

.site-header__cookies {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--surface-muted);
  border: none;
  border-radius: 999px;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 160ms, color 160ms;
}

.site-header__cookies:hover {
  background: var(--accent-soft);
  color: var(--accent);
}

.site-header__cookies-label {
  display: none;
}

@media (min-width: 700px) {
  .site-header__inner {
    padding: 0 24px;
  }
  .site-header__cookies-label {
    display: inline;
  }
}
</style>
